import React from 'react';
import classNames from 'classnames';
import {
  // These are svg react components
  AttributeAdvocateIcon as AttributeAdvocate,
  AttributeClientIcon as AttributeClient,
  AttributeCorporationIcon as AttributeCorporation,
  AttributeDonorIcon as AttributeDonor,
  AttributeFoundationIcon as AttributeFoundation,
  AttributeGovernmentIcon as AttributeGovernment,
  AttributeMajorDonorIcon as AttributeMajorDonor,
  AttributePartnerIcon as AttributePartner,
  AttributeRecurringIcon as AttributeRecurring,
  AttributeVipIcon as AttributeVip,
  // These are pngs
  BoardMemberImage,
  MemberImage,
  VolunteerImage,
} from '@/assets/images';

export enum AttributeIconSize {
  SMALL = 'small',
  MEDIUM = 'medium',
}

export enum AttributeIconType {
  DONOR = 'donor',
  MAJOR_DONOR = 'majorDonor',
  RECURRING = 'recurring',
  BOARD_MEMBER = 'boardMember',
  MEMBER = 'member',
  VOLUNTEER = 'volunteer',
  ADVOCATE = 'advocate',
  CORPORATION = 'corporation',
  FOUNDATION = 'foundation',
  GOVERNMENT = 'government',
  RECIPIENT = 'recipient',
  PARTNER = 'partner',
  VIP = 'vip',
}

const IconMap = {
  [AttributeIconType.ADVOCATE]: AttributeAdvocate,
  [AttributeIconType.CORPORATION]: AttributeCorporation,
  [AttributeIconType.DONOR]: AttributeDonor,
  [AttributeIconType.FOUNDATION]: AttributeFoundation,
  [AttributeIconType.GOVERNMENT]: AttributeGovernment,
  [AttributeIconType.MAJOR_DONOR]: AttributeMajorDonor,
  [AttributeIconType.RECIPIENT]: AttributeClient,
  [AttributeIconType.RECURRING]: AttributeRecurring,
  [AttributeIconType.PARTNER]: AttributePartner,
  [AttributeIconType.VIP]: AttributeVip,

  // These are going to be url strings
  [AttributeIconType.BOARD_MEMBER]: BoardMemberImage,
  [AttributeIconType.MEMBER]: MemberImage,
  [AttributeIconType.VOLUNTEER]: VolunteerImage,
};

export default function AttributeIcon({
  className,
  size = AttributeIconSize.SMALL,
  type,
}: {
  className?: string;
  size?: AttributeIconSize;
  type: AttributeIconType;
}) {
  const iconClasses = React.useMemo(
    () =>
      classNames({
        'h-3.5 w-3.5': size === AttributeIconSize.SMALL,
        'h-4.5 w-4.5': size === AttributeIconSize.MEDIUM,
      }),
    [size],
  );

  const renderIcon = () => {
    const Icon = IconMap[type];
    const props = {
      className: iconClasses,
      'data-testid': `attribute-icon-${type}`,
    };
    return typeof IconMap[type] === 'string' ? (
      <img src={Icon} {...props} alt="" />
    ) : (
      <Icon {...props} />
    );
  };

  return (
    <div
      className={classNames(
        className,
        'inline-flex items-center justify-center rounded text-primary-contrastText',
        {
          'h-5 w-5': size === AttributeIconSize.SMALL,
          'h-6 w-6': size === AttributeIconSize.MEDIUM,
          'bg-attribute-boardMember': type === AttributeIconType.BOARD_MEMBER,
          'bg-attribute-member': type === AttributeIconType.MEMBER,
          'bg-attribute-volunteer': type === AttributeIconType.VOLUNTEER,
          'bg-attribute-automation': [
            AttributeIconType.DONOR,
            AttributeIconType.MAJOR_DONOR,
            AttributeIconType.RECURRING,
          ].includes(type),
          'bg-attribute-advocate': type === AttributeIconType.ADVOCATE,
          'bg-attribute-corporation': type === AttributeIconType.CORPORATION,
          'bg-attribute-foundation': type === AttributeIconType.FOUNDATION,
          'bg-attribute-government': type === AttributeIconType.GOVERNMENT,
          'bg-attribute-recipient': type === AttributeIconType.RECIPIENT,
          'bg-attribute-partner': type === AttributeIconType.PARTNER,
          'bg-attribute-vip': type === AttributeIconType.VIP,
        },
      )}
    >
      {renderIcon()}
    </div>
  );
}
